import React, { useState, useEffect } from "react";
import DetailPageMiniImages from "./DetailPageMiniImages";
import DetailPageOffers from "./DetailPageOffers";
import DetailPageSizes from "./DetailPageSizes";
import { Product } from "../../../../Redux/ecommersProduct/types";
import { useCart } from "../../../../context/CartContext";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  AddToCartRequest,
  ProductType,
} from "../../../../Redux/custmoer_cart_service/types";

interface ProDetailModalProps {
  product: Product | null;
}

const DEFAULT_FALLBACK_IMAGES = [
  "../../assets/img/women/women-1.png",
  "../../assets/img/women/women-details/detail-mini-2.png",
  "../../assets/img/women/women-details/detail-mini-3.png",
  "../../assets/img/women/women-details/detail-mini-4.png",
  "../../assets/img/women/women-details/detail-mini-5.png",
  "../../assets/img/women/women-details/detail-mini-6.png",
];

const DEFAULT_FALLBACK_PRODUCT: any = {
  _id: "default-fallback-product",
  name: "GRECIILOOKS Women’s Tops – Crop Top, Loose Fit Oversized Shirts & Stylish Korean.",
  mainImage: "../../assets/img/women/women-1.png",
  vendors: { marketedBy: "GRECIILOOKS" },
  variations: [
    {
      ssin: "DEFAULT-SSIN",
      sku: "DEFAULT-SKU",
      pricing: { unitPrice: 2200, offerPrice: 1100, finalPrice: 1100 },
      media: { images: DEFAULT_FALLBACK_IMAGES },
    },
  ],
  reviews: { averageRating: 5.0, totalReviews: 1500 },
};

const ProDetailModal: React.FC<ProDetailModalProps> = ({ product }) => {
  const { addItemToCart, loading: cartLoading } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const displayProduct = product || DEFAULT_FALLBACK_PRODUCT;

  const [selectedImage, setSelectedImage] = useState<string>(
    displayProduct.mainImage || DEFAULT_FALLBACK_IMAGES[0]
  );

  useEffect(() => {
    if (displayProduct?.mainImage) {
      setSelectedImage(displayProduct.mainImage);
    }
  }, [product]);

  const currentVariation = displayProduct.variations?.[0];
  const pricing = currentVariation?.pricing;
  const offer = (displayProduct as any).featuredOffer;

  const finalPrice =
    offer?.finalPrice ?? pricing?.finalPrice ?? pricing?.unitPrice ?? 1100;
  const unitPrice =
    offer?.mrp ?? offer?.unitPrice ?? pricing?.unitPrice ?? 2200;
  const discount =
    offer?.discountPercent ??
    (unitPrice > finalPrice
      ? Math.round(((unitPrice - finalPrice) / unitPrice) * 100)
      : 50);

  const marketedBy =displayProduct.brand?.name;
  const averageRating = displayProduct.reviews?.averageRating || 0;
  const totalReviews = displayProduct.reviews?.totalReviews;
  const formattedTotalReviews =
    totalReviews && totalReviews >= 1000
      ? `${(totalReviews / 1000).toFixed(1)}k`
      : totalReviews
      ? `${totalReviews}`
      : "0";

  // Extract unique colors from variations
  const colors = Array.from(
    new Set(
      displayProduct.variations
        ?.map(
          (v: any) =>
            v.attributes?.["Color"] ||
            v.attributes?.["color"] ||
            v.attributes?.["Colour"]
        )
        .filter(Boolean)
    ) || []
  );

  const rawImages = [
    displayProduct.mainImage,
    ...(displayProduct.variations?.[0]?.media?.images || []),
  ].filter((img, i, arr) => img && arr.indexOf(img) === i);

  const allImages =
    rawImages.length > 0 ? rawImages : DEFAULT_FALLBACK_IMAGES;

  const handleAddToCart = async (currentProduct: any) => {
    if (!isAuthenticated) {
      const modalElement = document.getElementById("proDetailModal");
      if (modalElement) {
        const bootstrap = (window as any).bootstrap;
        if (bootstrap) {
          const modalInstance = bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) modalInstance.hide();
        }
      }
      navigate("/SignIn");
      return;
    }
    const currentVar = currentProduct.variations?.[0];
    const currentOffer = currentProduct.featuredOffer;
    const addToCartData: AddToCartRequest = {
      productId: currentProduct._id || "default-fallback-product",
      productName: currentProduct.name,
      productImage: currentProduct.mainImage,
      productType: ProductType.PHYSICAL,
      variantId:
        currentVar?.ssin ||
        currentOffer?.variantId ||
        currentProduct._id ||
        "DEFAULT-SSIN",
      variantName: currentVar?.sku || currentProduct.name,
      quantity: 1,
      unitPrice:
        currentVar?.pricing?.finalPrice || currentOffer?.finalPrice || 1100,
      sellerId: currentProduct.creatorId || "default-seller",
      sellerName:
        currentProduct.vendors?.marketedBy ||
        currentOffer?.sellerName ||
        "GRECIILOOKS",
      isMLMProduct: false,
      isAdminProduct: currentProduct.createdBy === "ADMIN",
    };

    try {
      const resultAction = await addItemToCart(addToCartData);
      if ((resultAction as any)?.type?.endsWith("/fulfilled")) {
        const modalElement = document.getElementById("proDetailModal");
        if (modalElement) {
          const bootstrap = (window as any).bootstrap;
          if (bootstrap) {
            const modalInstance = bootstrap.Modal.getInstance(modalElement);
            if (modalInstance) modalInstance.hide();
          }
        }
        window.location.href = "/Cart";
      } else {
        alert("Failed to add item to cart");
      }
    } catch (error) {
      console.error("Add to cart error:", error);
    }
  };

  const renderStars = (rating: number = averageRating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      let iconClass = "bi-star text-gray-200";
      if (i <= Math.floor(rating)) {
        iconClass = "bi-star-fill text-yellow";
      } else if (i - rating <= 0.5) {
        iconClass = "bi-star-half text-yellow";
      }
      stars.push(
        <i
          key={i}
          className={`bi ${iconClass} text-xxs ms-1`}
        ></i>
      );
    }
    return stars;
  };

  return (
    <div
      className="modal fade"
      id="proDetailModal"
      tabIndex={-1}
      aria-labelledby="proDetailModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content border-radius-none">
          <div className="modal-body">
            <div className="d-flex justify-content-start align-items-start gap-2">
              <div>
                <div className="d-flex flex-column justify-content-start align-items-center gap-2 px-2">
                  <div>
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      <div className="max-img-pro">
                        <img
                          src={selectedImage || displayProduct.mainImage}
                          alt={displayProduct.name}
                          className="max-image-pro-mini p-2"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src =
                              "../../../assets/img/salarlogo.png";
                          }}
                        />
                      </div>
                    </a>
                  </div>
                  <div className="w-100">
                    <DetailPageMiniImages
                      images={allImages}
                      selectedImage={selectedImage || displayProduct.mainImage}
                      setSelectedImage={setSelectedImage}
                    />
                  </div>
                </div>
              </div>
              <div className="text-center mx-auto w-100 mt-3 mb-3">
                <div className="lh-0 mb-2">
                  <h3 className="text-black fw-normal">{marketedBy}</h3>
                  <div className="mt-n2">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      <b className="text-normal text-xs text-decoration-line text-success">
                        Visit the {marketedBy} Store
                      </b>
                    </a>
                  </div>
                </div>
                <div className="lh-sm text-center mb-1">
                  <b className="text-black fw-normal fs-5">
                    {displayProduct.name}
                  </b>
                </div>
                <div className="mt-2">
                  <div className="text-sm">
                    <b className="fw-normal text-black fs-7">
                      {averageRating.toFixed(1)}
                    </b>
                    <span className="ms-1">
                      <a href="#" onClick={(e) => e.preventDefault()}>
                        {renderStars()}
                        <i className="bi bi-chevron-down fw-bold"></i>
                      </a>
                    </span>{" "}
                    <b className="fw-normal text-black fs-7">
                      ({formattedTotalReviews})
                    </b>
                  </div>
                  <div className="lh-0 mt-2 mb-2">
                    <h2 className="text-black fw-normal">
                      <sup className="fs-4 fw-light me-0">₹</sup>
                      {typeof finalPrice === "number"
                        ? finalPrice.toLocaleString()
                        : finalPrice}
                    </h2>
                    <div>
                      <b className="text-danger fs-5 fw-normal">
                        <b className="text-xs text-black me-1 fw-normal">
                          MRP:{" "}
                          <b className="text-xs text-black text-decoration-line-through fw-normal">
                            ₹
                            {typeof unitPrice === "number"
                              ? unitPrice.toLocaleString()
                              : unitPrice}
                          </b>
                        </b>
                        {discount > 0 && ` (-${discount}% off)`}
                      </b>
                      <p className="text-xxs fw-normal text-black mt-1">
                        Price inclusive of all taxes
                      </p>
                    </div>
                  </div>
                  <DetailPageOffers />
                  <h6 className="text-black mt-2">Colors</h6>
                  <div className="d-flex justify-content-center gap-3 align-items-center mb-2">
                    {colors.length > 0 ? (
                      colors.map((color: any, idx) => (
                        <div
                          className="content-highlight cursor-pointer"
                          key={idx}
                        >
                          <span
                            className="rounded-circle avatar avatar-mm mt-1 border"
                            style={{
                              backgroundColor: String(color).toLowerCase(),
                              display: "inline-block",
                            }}
                            title={String(color)}
                          ></span>
                        </div>
                      ))
                    ) : (
                      <div className="text-xxs text-muted">No colors available</div>
                    )}
                  </div>
                  <h6 className="text-black mt-2">Sizes</h6>
                  <div className="d-flex justify-content-center gap-2 align-items-center mb-2">
                    <DetailPageSizes product={displayProduct} />
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    className="btn btn-xl border-radius-section w-75 btn-dark"
                    onClick={() => handleAddToCart(displayProduct)}
                    disabled={cartLoading}
                  >
                    {cartLoading ? "Adding..." : "Add to Bag"}
                  </button>
                </div>
                <div className="mt-2">
                  <a href={`/DetailPage/${displayProduct._id}`}>
                    <button className="btn btn-xl p-2 border-radius-section w-75 btn-outline-dark">
                      Product Details
                    </button>
                  </a>
                </div>
                <div className="mt-3">
                  {[
                    { icon: "bi-whatsapp" },
                    { icon: "bi-instagram" },
                    { icon: "bi-facebook" },
                    { icon: "bi-twitter-x" },
                  ].map(({ icon }, idx) => (
                    <li
                      className="avatar avatar-sm border rounded-circle bg-white me-2 d-inline-flex align-items-center justify-content-center"
                      key={idx}
                      style={{ listStyleType: "none" }}
                    >
                      <a
                        href="#"
                        onClick={(e) => e.preventDefault()}
                        className="d-flex align-items-center justify-content-center w-100 h-100"
                      >
                        <i className={`bi ${icon} fs-6 text-black`}></i>
                      </a>
                    </li>
                  ))}
                </div>
              </div>
              <div className="w-10 text-end">
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="fi fi-rr-cross text-black"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProDetailModal;
